import { useEffect, useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import * as api from '@gitbook/api-client';
import {
    Description,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogRoot,
    FieldError,
    Fieldset,
    Form,
    Label,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { apiClient } from '../../../state';
import { SpaceCreationDialog } from '../../Space';
import { SpacePickerField } from './SpacePickerField';

type SectionFormData = {
    title: string;
    spaceId: string | null;
};

export interface CreateSiteVariantFormProps {
    spaces: api.Space[];
    organization: api.Organization;
    site: api.Site;
    siteSection: api.SiteSection | null;
    defaultSpaceId: string | null;
    onCreateVariant?: (siteSpace: api.SiteSpace, siteSection: api.SiteSection | null) => void;
}

export function CreateSiteVariantForm(props: CreateSiteVariantFormProps) {
    const { spaces, site, organization, siteSection, defaultSpaceId, onCreateVariant } = props;

    const form = useForm<SectionFormData>({
        defaultValues: {
            title: '',
            spaceId: defaultSpaceId,
        },
    });
    const spaceId = form.watch('spaceId');
    const space = useMemo(() => {
        if (!spaceId) {
            return null;
        }
        return spaces.find((space) => space.id === spaceId) ?? null;
    }, [spaceId, spaces]);
    useEffect(() => {
        if (space && !form.getFieldState('title').isTouched) {
            form.setValue('title', space.title);
        }
    }, [space, form]);
    const [isPending, startDialogAction] = useDialogAction();
    const onSubmit: SubmitHandler<SectionFormData> = async (data) => {
        const { spaceId } = data;
        await startDialogAction(async ({ close }) => {
            assertIsDefined(spaceId, 'Space ID should be defined at this point');
            await toast.catch(async () => {
                const { data: siteSpace } = await apiClient.siteSpaces.addSpaceToSite(
                    organization.id,
                    site.id,
                    {
                        spaceId,
                        sectionId: siteSection?.id,
                    }
                );
                await apiClient.siteCustomization.overrideSiteSpaceCustomizationById(
                    organization.id,
                    site.id,
                    siteSpace.id,
                    { title: data.title }
                );
                onCreateVariant?.(siteSpace, siteSection);
            }, 'Error while adding site variant');
            close();
        });
    };
    const createSpaceDialogTrigger = useDialogTriggerState();
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <Fieldset>
                    <DialogRoot {...createSpaceDialogTrigger.rootProps}>
                        <SpacePickerField
                            form={form}
                            name="spaceId"
                            spaces={spaces}
                            disabled={isPending}
                            description="Select the content of this section."
                            createSpaceDialogTrigger={createSpaceDialogTrigger}
                            onlyCreateIfEmpty
                        />
                        <SpaceCreationDialog
                            organizationId={organization.id}
                            target={{
                                type: 'siteVariant',
                                props: {
                                    site,
                                    siteSection: props.siteSection,
                                    onCreateSpace: (space) => {
                                        form.setValue('spaceId', space.id, {
                                            shouldDirty: true,
                                            shouldTouch: true,
                                        });
                                    },
                                },
                            }}
                        />
                    </DialogRoot>
                    <TextField
                        form={form}
                        name="title"
                        required
                        minLength={api.SITE_TITLE_MIN_LENGTH}
                        maxLength={api.SITE_TITLE_MAX_LENGTH}
                    >
                        <Label>Title</Label>
                        <Description>
                            The title that will appear in the variants dropdown menu.
                        </Description>
                        <TextInput disabled={isPending} />
                        <FieldError />
                    </TextField>
                </Fieldset>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Add" />
            </DialogFooter>
        </Form>
    );
}
